(function () {
  if (customElements.get('premium-hero')) return;

  class PremiumHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.viewport = this.querySelector('[data-hero-viewport]');
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.bullets = Array.from(this.querySelectorAll('[data-hero-bullet]'));
      this.videos = Array.from(this.querySelectorAll('.premium-hero__video'));
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number(this.dataset.speed) || 7000;
      this.paused = false;
      this.pointerStartX = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.stop = this.stop.bind(this);
      this.start = this.start.bind(this);
      this.togglePause = this.togglePause.bind(this);

      if (this.slides.length < 2) return;

      this.viewport.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
      this.addEventListener('mouseenter', this.stop);
      this.addEventListener('mouseleave', this.start);
      this.addEventListener('focusin', this.stop);
      this.addEventListener('focusout', this.start);
      this.pauseButton.addEventListener('click', this.togglePause);
      this.bullets.forEach((bullet) => {
        bullet.addEventListener('click', () => {
          this.show(Number(bullet.dataset.index));
        });
      });
      this.videos.forEach((video) => video.addEventListener('ended', this.onVideoEnded));
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.syncVideos(true);
      this.start();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.videos.forEach((video) => {
        video.pause();
        video.removeEventListener('ended', this.onVideoEnded);
      });
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    show(index) {
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.bullets.forEach((bullet, bulletIndex) => {
        const active = bulletIndex === this.currentIndex;
        bullet.classList.toggle('is-active', active);
        bullet.setAttribute('aria-current', String(active));
      });
      this.syncVideos(true);
      this.stop();
      this.start();
    }

    next() {
      this.show(this.currentIndex + 1);
    }

    previous() {
      this.show(this.currentIndex - 1);
    }

    start() {
      if (!this.autoplay || this.paused || document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      window.clearInterval(this.timer);

      const activeVideo = this.getActiveVideo();
      if (activeVideo) {
        const playPromise = activeVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        return;
      }

      this.timer = window.setInterval(() => this.next(), this.speed);
    }

    stop() {
      window.clearInterval(this.timer);
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseButton.textContent = this.paused
        ? 'Продовжити автоматичну зміну слайдів'
        : 'Зупинити автоматичну зміну слайдів';
      if (this.paused) {
        this.stop();
        this.pauseActiveVideo();
      } else {
        this.start();
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stop();
        this.pauseActiveVideo();
      } else {
        this.start();
      }
    }

    onVideoEnded(event) {
      const activeVideo = this.getActiveVideo();
      if (event.currentTarget !== activeVideo) return;
      if (!this.autoplay || this.paused || document.hidden) return;
      this.next();
    }

    getActiveVideo() {
      const activeSlide = this.slides[this.currentIndex];
      return activeSlide ? activeSlide.querySelector('.premium-hero__video') : null;
    }

    pauseActiveVideo() {
      const activeVideo = this.getActiveVideo();
      if (activeVideo) activeVideo.pause();
    }

    syncVideos(restartActiveVideo) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const canPlay = !this.paused && !document.hidden && !reduceMotion;

      this.slides.forEach((slide, slideIndex) => {
        const video = slide.querySelector('.premium-hero__video');
        if (!video) return;

        if (slideIndex !== this.currentIndex || !canPlay) {
          video.pause();
          if (video.currentTime !== 0) video.currentTime = 0;
          return;
        }

        if (restartActiveVideo) video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      }
    }

    onPointerDown(event) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label')) {
        this.pointerStartX = null;
        return;
      }

      this.pointerStartX = event.clientX;
    }

    onPointerUp(event) {
      if (!event.isPrimary) return;
      if (this.pointerStartX === null) return;
      if (event.target.closest('a, button, input, select, textarea, label')) {
        this.pointerStartX = null;
        return;
      }

      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 45) return;
      distance < 0 ? this.next() : this.previous();
    }

    onPointerCancel() {
      this.pointerStartX = null;
    }
  }

  customElements.define('premium-hero', PremiumHero);
})();
